class AccessibilityWidget {
  constructor() {
    this.widget = document.querySelector('.tnr-a11y-widget');
    if (!this.widget) return;

    this.trigger = this.widget.querySelector('.tnr-a11y-widget__trigger');
    this.panel = this.widget.querySelector('.tnr-a11y-widget__panel');
    this.closeBtn = this.widget.querySelector('.tnr-a11y-widget__close');
    this.resetBtn = this.widget.querySelector('.tnr-a11y-widget__reset');
    this.buttons = this.widget.querySelectorAll('.tnr-a11y-widget__btn');

    this.state = this.loadState();

    this.initReadingGuide();
    this.initTTS();
    this.initPageNav();

    this.applyState();
    this.bindEvents();

    // Periodically enforce mute if enabled (in case new media loads dynamically)
    setInterval(() => {
      if (this.state.muteMedia) this.enforceMute();
    }, 1000);
  }

  loadState() {
    const defaultState = {
      textSize: 0,
      highContrast: false,
      grayscale: false,
      underlineLinks: false,
      highlightLinks: false,
      reduceMotion: false,
      enhanceFocus: false,
      dyslexiaFont: false,
      textSpacing: false,
      readingGuide: false,
      tts: false,
      colorBlindMode: 'none', // none | protanopia | deuteranopia | tritanopia
      hideMedia: false,
      muteMedia: false,
      smartDark: false,
      bigCursor: false,
      boldText: false,
      paraHighlight: false,
      hitTargets: false,
      pageNav: false
    };
    try {
      const stored = localStorage.getItem('tnr-a11y-state');
      return stored ? { ...defaultState, ...JSON.parse(stored) } : defaultState;
    } catch (e) {
      return defaultState;
    }
  }

  saveState() {
    try {
      localStorage.setItem('tnr-a11y-state', JSON.stringify(this.state));
    } catch (e) {
      console.warn('Could not save a11y state to localStorage');
    }
  }

  applyState() {
    const html = document.documentElement;

    // Text size
    html.classList.remove('a11y-text-1', 'a11y-text-2', 'a11y-text-3');
    if (this.state.textSize > 0) html.classList.add(`a11y-text-${this.state.textSize}`);

    // Color Blindness (cycle through)
    html.classList.remove('a11y-cb-protanopia', 'a11y-cb-deuteranopia', 'a11y-cb-tritanopia');
    if (this.state.colorBlindMode !== 'none') html.classList.add(`a11y-cb-${this.state.colorBlindMode}`);

    // Boolean toggles
    const toggles = [
      'highContrast', 'grayscale', 'underlineLinks', 'highlightLinks',
      'reduceMotion', 'enhanceFocus', 'dyslexiaFont', 'textSpacing',
      'readingGuide', 'tts', 'hideMedia', 'smartDark',
      'bigCursor', 'boldText', 'paraHighlight', 'hitTargets'
    ];
    const classMap = {
      highContrast: 'a11y-high-contrast',
      grayscale: 'a11y-grayscale',
      underlineLinks: 'a11y-underline-links',
      highlightLinks: 'a11y-highlight-links',
      reduceMotion: 'a11y-reduce-motion',
      enhanceFocus: 'a11y-enhance-focus',
      dyslexiaFont: 'a11y-dyslexia-font',
      textSpacing: 'a11y-text-spacing',
      readingGuide: 'a11y-reading-guide',
      tts: 'a11y-tts-active',
      hideMedia: 'a11y-hide-media',
      smartDark: 'a11y-smart-dark',
      bigCursor: 'a11y-big-cursor',
      boldText: 'a11y-bold-text',
      paraHighlight: 'a11y-para-highlight',
      hitTargets: 'a11y-hit-targets'
    };
    toggles.forEach(key => html.classList.toggle(classMap[key], !!this.state[key]));

    // Page Navigator
    html.classList.toggle('a11y-page-nav-open', !!this.state.pageNav);
    if (this.state.pageNav) this.buildPageNav();

    // Stop TTS if toggled off
    if (!this.state.tts && window.speechSynthesis) window.speechSynthesis.cancel();

    // Enforce mute
    if (this.state.muteMedia) this.enforceMute();

    // Sync button pressed states
    this.buttons.forEach(btn => {
      const action = btn.dataset.a11yAction;
      if (!action) return;
      if (action === 'textSize') {
        btn.setAttribute('aria-pressed', this.state.textSize > 0);
        const lvl = btn.querySelector('.a11y-level');
        if (lvl) lvl.textContent = this.state.textSize > 0 ? ` (${this.state.textSize})` : '';
      } else if (action === 'colorBlindMode') {
        btn.setAttribute('aria-pressed', this.state.colorBlindMode !== 'none');
        const span = btn.querySelector('span');
        if (span) {
          const names = { none: 'Color Blindness', protanopia: 'Protanopia', deuteranopia: 'Deuteranopia', tritanopia: 'Tritanopia' };
          span.textContent = names[this.state.colorBlindMode] || 'Color Blindness';
        }
      } else {
        btn.setAttribute('aria-pressed', !!this.state[action]);
      }
    });
  }

  enforceMute() {
    document.querySelectorAll('audio, video').forEach(media => {
      media.muted = true;
      if (media.tagName === 'AUDIO') media.pause();
    });
  }

  bindEvents() {
    this.trigger.addEventListener('click', () => this.togglePanel());
    this.closeBtn.addEventListener('click', () => this.closePanel());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.panel.getAttribute('aria-hidden') === 'false') {
        this.closePanel();
        this.trigger.focus();
      }
    });

    document.addEventListener('click', (e) => {
      if (this.panel.getAttribute('aria-hidden') === 'false' && !this.widget.contains(e.target)) {
        this.closePanel();
      }
    });

    this.buttons.forEach(btn => {
      btn.addEventListener('click', () => {
        const action = btn.dataset.a11yAction;
        if (!action) return;
        if (action === 'textSize') {
          this.state.textSize = (this.state.textSize + 1) % 4;
        } else if (action === 'colorBlindMode') {
          const modes = ['none', 'protanopia', 'deuteranopia', 'tritanopia'];
          const idx = modes.indexOf(this.state.colorBlindMode);
          this.state.colorBlindMode = modes[(idx + 1) % modes.length];
        } else if (this.state.hasOwnProperty(action)) {
          this.state[action] = !this.state[action];
        }
        this.saveState();
        this.applyState();
      });
    });

    this.resetBtn.addEventListener('click', () => {
      this.state = {
        textSize: 0, highContrast: false, grayscale: false,
        underlineLinks: false, highlightLinks: false, reduceMotion: false,
        enhanceFocus: false, dyslexiaFont: false, textSpacing: false,
        readingGuide: false, tts: false, colorBlindMode: 'none',
        hideMedia: false, muteMedia: false, smartDark: false,
        bigCursor: false, boldText: false, paraHighlight: false,
        hitTargets: false, pageNav: false
      };
      this.saveState();
      this.applyState();
    });
  }

  togglePanel() {
    const isHidden = this.panel.getAttribute('aria-hidden') === 'true';
    if (isHidden) { this.openPanel(); } else { this.closePanel(); }
  }

  openPanel() {
    this.panel.setAttribute('aria-hidden', 'false');
    this.trigger.setAttribute('aria-expanded', 'true');
    setTimeout(() => { this.closeBtn && this.closeBtn.focus(); }, 100);
  }

  closePanel() {
    this.panel.setAttribute('aria-hidden', 'true');
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  // ── Reading Guide ───────────────────────────────────────────
  initReadingGuide() {
    this.guideTop = document.createElement('div');
    this.guideTop.className = 'tnr-a11y-reading-guide-top';
    this.guideBottom = document.createElement('div');
    this.guideBottom.className = 'tnr-a11y-reading-guide-bottom';
    document.body.appendChild(this.guideTop);
    document.body.appendChild(this.guideBottom);
    const maskHeight = 100;
    document.addEventListener('mousemove', (e) => {
      if (!this.state.readingGuide) return;
      this.guideTop.style.height = `${e.clientY - maskHeight / 2}px`;
      this.guideBottom.style.top = `${e.clientY + maskHeight / 2}px`;
    });
  }

  // ── Text-to-Speech ─────────────────────────────────────────
  initTTS() {
    if (!window.speechSynthesis) return;
    document.addEventListener('click', (e) => {
      if (!this.state.tts) return;
      if (this.widget.contains(e.target)) return;
      if (e.target.closest('a') || e.target.closest('button')) e.preventDefault();
      window.speechSynthesis.cancel();
      const text = e.target.innerText || e.target.textContent || e.target.alt || '';
      if (text.trim()) {
        const utt = new SpeechSynthesisUtterance(text.trim());
        window.speechSynthesis.speak(utt);
      }
    });
  }

  // ── Page Structure Navigator ───────────────────────────────
  initPageNav() {
    this.pageNavEl = document.createElement('nav');
    this.pageNavEl.className = 'tnr-a11y-page-nav';
    this.pageNavEl.setAttribute('aria-label', 'Page structure');
    this.pageNavEl.innerHTML = `
      <div class="tnr-a11y-page-nav__title">
        Page Structure
        <button class="tnr-a11y-page-nav__close" aria-label="Close page navigator">✕</button>
      </div>
      <ul class="tnr-a11y-page-nav__list" id="tnr-a11y-nav-list"></ul>`;
    document.body.appendChild(this.pageNavEl);

    this.pageNavEl.querySelector('.tnr-a11y-page-nav__close').addEventListener('click', () => {
      this.state.pageNav = false;
      this.saveState();
      this.applyState();
    });
  }

  buildPageNav() {
    const list = document.getElementById('tnr-a11y-nav-list');
    if (!list) return;
    const headings = document.querySelectorAll('main h1, main h2, main h3, main h4, h1, h2, h3, h4');
    list.innerHTML = '';
    if (headings.length === 0) {
      list.innerHTML = '<li style="opacity:0.6;padding:4px;">No headings found on this page.</li>';
      return;
    }
    headings.forEach((h, i) => {
      if (!h.id) h.id = `a11y-heading-${i}`;
      const li = document.createElement('li');
      li.className = `tnr-a11y-page-nav__item tnr-a11y-page-nav__item--${h.tagName.toLowerCase()}`;
      li.innerHTML = `<a href="#${h.id}">${h.textContent.trim()}</a>`;
      li.querySelector('a').addEventListener('click', (e) => {
        e.preventDefault();
        h.scrollIntoView({ behavior: 'smooth', block: 'start' });
        h.setAttribute('tabindex', '-1');
        h.focus({ preventScroll: true });
      });
      list.appendChild(li);
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new AccessibilityWidget();
});
